/**
 * BFF Client — Typed fetch() wrapper for Hono BFF endpoints.
 *
 * Replaces direct Server Action calls with HTTP requests to the Hono BFF.
 * All requests include credentials (HTTP-only auth cookie) automatically.
 *
 * Both the POST `endpoint`/`body` AND the response are keyed by the two-sided
 * `BffContract` (the SSOT for every BFF round-trip), so an unknown endpoint, a
 * body that doesn't match what the server reads, OR a response shape that drifts
 * from what the server returns is a COMPILE error — the listings-search class of
 * "client/server silently drifted from the proto" can't recur on either side.
 * There is no free caller-supplied response generic: the contract supplies it.
 *
 * The response is PARSED, not cast (R5 W6 PR-4): every body is validated against
 * the contract's runtime twin (`bff-schemas.ts`, compiler-proven identical to
 * `BffContract`). A response that doesn't match the contract becomes a
 * structured `{ success: false, error }` result (or a thrown error for
 * `bffGet`) instead of silently flowing into typed code. Parsing the error
 * envelope also FORWARDS the server's structured `ServerErrorInfo` on non-2xx
 * responses — the old wrapper collapsed every non-2xx to `HTTP <status>`,
 * discarding the typed `reason` the route had decoded for localization.
 */
import type { ServerErrorInfo } from '@letsgethome/lib/server-errors';
import * as z from 'zod/mini';

import type {
  BffBodyArgs,
  BffEndpoint,
  BffGetContract,
  BffGetEndpoint,
  BffResponse,
} from './bff-contract';
import {
  bffGetResponseSchema,
  bffResponseSchema,
  serverErrorInfoSchema,
  summarizeSchemaError,
} from './bff-schemas';

export interface BffResult<T = unknown> {
  success: boolean;
  data?: T;
  /**
   * Structured server error: the English `message` + the typed `reason` +
   * `metadata` decoded from `common.v1.ErrorInfo` and forwarded by the BFF route
   * (the lossy `error: string` is gone). Consumers localize via the typed
   * `reason` (`localizeServerError` / `localizeApiError`); the union typing makes
   * reading `error.reason` compile-checked.
   */
  error?: ServerErrorInfo;
}

/**
 * The wire envelope, validated: `{ success: true, data? }` (data checked against
 * the endpoint's contract response schema) or `{ success: false, error }`.
 * Discriminated on `success`, so a success envelope with an off-contract `data`
 * fails parsing instead of leaking an unchecked shape to the caller.
 */
function envelopeSchema<E extends BffEndpoint>(endpoint: E) {
  return z.discriminatedUnion('success', [
    z.object({ success: z.literal(true), data: z.optional(bffResponseSchema(endpoint)) }),
    z.object({ success: z.literal(false), error: serverErrorInfoSchema }),
  ]);
}

/** Read a response body as JSON, or `undefined` when it isn't JSON at all. */
async function readJson(res: Response): Promise<unknown> {
  try {
    return (await res.json()) as unknown;
  } catch {
    return undefined;
  }
}

export async function bff<E extends BffEndpoint>(
  endpoint: E,
  ...[body]: BffBodyArgs<E>
): Promise<BffResult<BffResponse<E>>> {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  // Parse the envelope REGARDLESS of HTTP status: the route emits its structured
  // `{ success: false, error }` envelope with a non-2xx status, and dropping it
  // on `!res.ok` would strip the typed `reason` consumers localize with.
  const parsed = envelopeSchema(endpoint).safeParse(await readJson(res));
  if (parsed.success) {
    return parsed.data;
  }
  if (!res.ok) {
    // Non-2xx without a valid envelope (proxy error page, non-BFF middleware).
    return { success: false, error: { message: `HTTP ${res.status}` } };
  }
  // A 2xx whose body is off-contract: fail LOUD with a structured error — never
  // hand an unvalidated shape to the typed caller (the old cast did exactly that).
  return {
    success: false,
    error: {
      message: `Malformed BFF response from ${endpoint}: ${summarizeSchemaError(parsed.error)}`,
    },
  };
}

export async function bffGet<E extends BffGetEndpoint>(endpoint: E): Promise<BffGetContract[E]> {
  const res = await fetch(endpoint, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  // GET handlers return the view-model directly (no `BffResult` envelope); the
  // contract's runtime schema validates the body at this single parse boundary.
  const parsed = bffGetResponseSchema(endpoint).safeParse(await readJson(res));
  if (!parsed.success) {
    throw new Error(
      `Malformed BFF response from ${endpoint}: ${summarizeSchemaError(parsed.error)}`
    );
  }
  return parsed.data;
}
